import { useState, useEffect, useRef } from "react";
import { useRealTimeUnrealizedPnL } from "@/backend/store";

/**
 * Throttled version of unrealized P&L for portfolio page.
 * Updates at most once every 5 seconds to reduce visual noise.
 * 
 * ⚠️ Only use this on the Portfolio page - other pages should use
 * useRealTimeUnrealizedPnL directly for immediate updates.
 */
export const useThrottledUnrealizedPnL = (throttleMs: number = 5000) => {
  const realtimeValue = useRealTimeUnrealizedPnL();
  const [throttledValue, setThrottledValue] = useState(realtimeValue);
  const lastUpdateRef = useRef(Date.now());

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    if (timeSinceLastUpdate >= throttleMs) {
      // Enough time has passed, update immediately
      setThrottledValue(realtimeValue);
      lastUpdateRef.current = now;
    } else {
      // Schedule an update for when the throttle period expires
      const timeoutId = setTimeout(() => {
        setThrottledValue(realtimeValue);
        lastUpdateRef.current = Date.now();
      }, throttleMs - timeSinceLastUpdate);

      return () => clearTimeout(timeoutId);
    }
  }, [realtimeValue, throttleMs]);

  return throttledValue;
};

